import { WIDGET_BASE_URL } from "./constants"
import type { InitOptions, VinciWidgetInstance } from "./types"

// Auto-boot layer for the one-tag embed:
//
//   <script async src=".../palazzo-vinci-widget.js"
//     data-partner-slug="ashley"></script>
//
// When data-partner-slug is present on the loading script tag, this module
// renders a floating launcher button and owns the full authorize → start →
// close lifecycle. Without it, it does nothing at all — the programmatic
// VinciWidgetSDK API is unaffected.
//
// Two authorize modes (Intercom parity):
// - "public" (default, anonymous visitors): no partner backend at all. The
//   SDK POSTs the widget host's keyless /api/vinci-widget/public-authorize,
//   which is gated server-side by the partner's origin allowlist. A stable
//   visitorId (localStorage) maps the shopper to the same anonymous user
//   across sessions.
// - "custom" (identified users): the partner sets data-authorize-url to their
//   own same-origin route that holds their API key server-side and proxies
//   POST /external/vinci-widget/authorize with the shopper's identity.

export interface AutobootSDK {
  init(options?: InitOptions): VinciWidgetInstance
}

export type LauncherPosition = "bottom-right" | "bottom-left"

export type AuthorizeMode = "public" | "custom"

export interface AutobootConfig {
  partnerSlug: string
  authorizeUrl: string
  mode: AuthorizeMode
  position: LauncherPosition
}

interface AuthorizeResult {
  token: string
  conversationId: string
}

const LAUNCHER_ATTR = "data-palazzo-vinci-launcher"
const STORAGE_KEY_PREFIX = "palazzo-vinci-widget:conversation:"
const VISITOR_KEY_PREFIX = "palazzo-vinci-widget:visitor:"
export const PUBLIC_AUTHORIZE_PATH = "/api/vinci-widget/public-authorize"

// A session_expired arriving within this window of a silent re-auth restart is
// treated as the SECOND consecutive failure (bad/instantly-rejected token) and
// surfaces the launcher instead of looping. Past the window, the restart is
// considered successful and the retry latch resets, so a token expiring
// naturally later in the session gets its own silent re-auth. The widget makes
// authenticated requests as soon as it boots, so a broken restart fails well
// inside this window.
export const REAUTH_FAILURE_WINDOW_MS = 30_000

const LAUNCHER_ICON_SVG =
  '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M12 3C7.03 3 3 6.58 3 11c0 2.08.9 3.97 2.37 5.39-.13 1.25-.55 2.6-1.25 3.61 1.71-.16 3.4-.79 4.65-1.55.99.36 2.07.55 3.23.55 4.97 0 9-3.58 9-8s-4.03-8-9-8Z" ' +
  'fill="currentColor"/></svg>'

export function parseAutobootConfig(
  script: { getAttribute(name: string): string | null } | null,
  widgetBaseUrl: string = WIDGET_BASE_URL,
): AutobootConfig | null {
  if (!script) return null
  const partnerSlug = script.getAttribute("data-partner-slug")?.trim()
  if (!partnerSlug) return null
  const position: LauncherPosition =
    script.getAttribute("data-position") === "bottom-left"
      ? "bottom-left"
      : "bottom-right"

  const authorizeUrl = script.getAttribute("data-authorize-url")?.trim()
  if (!authorizeUrl) {
    // Default: keyless public authorize on the widget host itself.
    if (!widgetBaseUrl || widgetBaseUrl === "undefined") {
      console.error(
        "[VinciWidgetSDK] WIDGET_BASE_URL missing from the bundle; ignoring embed",
      )
      return null
    }
    return {
      partnerSlug,
      authorizeUrl: `${widgetBaseUrl}${PUBLIC_AUTHORIZE_PATH}`,
      mode: "public",
      position,
    }
  }

  // Custom mode sends credentials:"include", so the endpoint must be
  // same-origin; reject an absolute cross-origin value so a mistyped/hostile
  // attribute can't ship the partner's cookies to another origin. A relative
  // path is same-origin.
  let absolute: URL | null = null
  try {
    absolute = new URL(authorizeUrl)
  } catch {
    absolute = null
  }
  if (absolute && absolute.origin !== window.location.origin) {
    console.error(
      "[VinciWidgetSDK] data-authorize-url must be same-origin; ignoring embed",
    )
    return null
  }
  return { partnerSlug, authorizeUrl, mode: "custom", position }
}

function storageKey(partnerSlug: string): string {
  return `${STORAGE_KEY_PREFIX}${partnerSlug}`
}

// The visitorId lives in localStorage (not sessionStorage): it's what makes a
// returning anonymous shopper the same user server-side, Intercom-cookie style.
function readStoredVisitorId(partnerSlug: string): string | null {
  try {
    return window.localStorage.getItem(`${VISITOR_KEY_PREFIX}${partnerSlug}`)
  } catch {
    return null
  }
}

function storeVisitorId(partnerSlug: string, visitorId: string) {
  try {
    window.localStorage.setItem(`${VISITOR_KEY_PREFIX}${partnerSlug}`, visitorId)
  } catch {
    // localStorage unavailable — the server mints a fresh visitor per
    // authorize; the widget still works, just without cross-session identity.
  }
}

// Stored alongside visitorId in localStorage (was sessionStorage): a shopper
// returning in a new tab hands back the same conversation, and the server
// mints a new one whenever it declines the hint.
function readStoredConversationId(partnerSlug: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(partnerSlug))
  } catch {
    return null
  }
}

function storeConversationId(partnerSlug: string, conversationId: string) {
  try {
    window.localStorage.setItem(storageKey(partnerSlug), conversationId)
  } catch {
    // localStorage unavailable (sandboxed iframe, privacy mode) — in-memory
    // reuse still covers reopens within the page, and the public path resumes
    // the visitor's latest conversation server-side anyway.
  }
}

function createLauncher(position: LauncherPosition): {
  container: HTMLDivElement
  button: HTMLButtonElement
} {
  const container = document.createElement("div")
  container.setAttribute(LAUNCHER_ATTR, "")
  container.style.cssText = [
    "position:fixed",
    "bottom:24px",
    position === "bottom-left" ? "left:24px" : "right:24px",
    // One below the widget frame's z-index so an open widget always covers it.
    "z-index:2147483645",
  ].join(";")

  const button = document.createElement("button")
  button.setAttribute("type", "button")
  button.setAttribute("aria-label", "Chat with Vinci")
  button.style.cssText = [
    "width:56px",
    "height:56px",
    "border-radius:50%",
    "border:0",
    "background:#1c1917",
    "color:#fff",
    "cursor:pointer",
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "padding:0",
    "box-shadow:0 8px 24px rgba(0,0,0,0.28)",
  ].join(";")
  button.innerHTML = LAUNCHER_ICON_SVG

  container.appendChild(button)
  return { container, button }
}

export function bootLauncher(sdk: AutobootSDK, config: AutobootConfig): void {
  let widget: VinciWidgetInstance | null = null
  let conversationId: string | null = readStoredConversationId(
    config.partnerSlug,
  )
  // One-retry latch for silent re-auth after session_expired. While set, the
  // next session_expired is a consecutive failure → launcher, no loop. It
  // auto-resets REAUTH_FAILURE_WINDOW_MS after a silent restart (restart
  // deemed successful) and clears on launcher-click opens and user_closed.
  let reauthUsed = false
  let reauthResetTimer: ReturnType<typeof setTimeout> | null = null
  let opening = false

  const armReauthReset = () => {
    if (reauthResetTimer) clearTimeout(reauthResetTimer)
    reauthResetTimer = setTimeout(() => {
      reauthUsed = false
      reauthResetTimer = null
    }, REAUTH_FAILURE_WINDOW_MS)
  }

  const clearReauthState = () => {
    if (reauthResetTimer) {
      clearTimeout(reauthResetTimer)
      reauthResetTimer = null
    }
    reauthUsed = false
  }

  const { container, button } = createLauncher(config.position)

  const showLauncher = () => {
    container.style.display = ""
  }
  const hideLauncher = () => {
    container.style.display = "none"
  }

  async function authorize(): Promise<AuthorizeResult | null> {
    const body: Record<string, string> = {
      parentOrigin: window.location.origin,
    }
    if (conversationId) body.conversationId = conversationId
    if (config.mode === "public") {
      body.partnerSlug = config.partnerSlug
      const visitorId = readStoredVisitorId(config.partnerSlug)
      if (visitorId) body.visitorId = visitorId
    }
    try {
      const res = await fetch(config.authorizeUrl, {
        method: "POST",
        headers: { "content-type": "application/json" },
        // Public mode is a cross-origin call to the widget host — no partner
        // cookies belong there. Custom mode is same-origin (enforced at parse
        // time) and keeps cookies so the partner can identify the shopper.
        credentials: config.mode === "public" ? "omit" : "include",
        body: JSON.stringify(body),
      })
      if (!res.ok) return null
      const data = await res.json()
      if (!data?.token || !data?.conversationId) return null
      if (config.mode === "public" && typeof data.visitorId === "string") {
        storeVisitorId(config.partnerSlug, data.visitorId)
      }
      return { token: data.token, conversationId: data.conversationId }
    } catch {
      return null
    }
  }

  function rememberConversationId(returned: string): string {
    // The token is scoped to the conversation the server returns, so always
    // start with `returned` — including on re-auth. authorize() sends the
    // stored id as a resume hint, and the server honors it when it is still
    // this visitor's open conversation; when it declines, `returned` is a new
    // conversation. Trusting `returned` either way is what keeps
    // token <-> conversationId in sync (a stale id + fresh token is rejected).
    conversationId = returned
    storeConversationId(config.partnerSlug, returned)
    return returned
  }

  function teardown() {
    widget?.close()
    widget = null
  }

  function startWidget(token: string, cid: string) {
    const instance = sdk.init()
    widget = instance
    instance.start({
      userToken: token,
      partnerSlug: config.partnerSlug,
      conversationId: cid,
    })
    instance.on("user_closed", () => {
      teardown()
      clearReauthState()
      showLauncher()
    })
    instance.on("session_expired", () => {
      void handleSessionExpired()
    })
    instance.on("widget_error", (payload) => {
      console.error("[VinciWidgetSDK] widget_error", payload)
      teardown()
      showLauncher()
    })
  }

  async function handleSessionExpired() {
    teardown()
    if (reauthUsed) {
      // Second consecutive failure — give up silently reconnecting.
      clearReauthState()
      showLauncher()
      return
    }
    reauthUsed = true
    const auth = await authorize()
    if (!auth) {
      clearReauthState()
      showLauncher()
      return
    }
    const cid = rememberConversationId(auth.conversationId)
    startWidget(auth.token, cid)
    armReauthReset()
  }

  async function handleLauncherClick() {
    if (opening || widget) return
    opening = true
    try {
      const auth = await authorize()
      if (!auth) {
        console.error(
          "[VinciWidgetSDK] authorize request failed — widget not started",
        )
        return
      }
      const cid = rememberConversationId(auth.conversationId)
      clearReauthState()
      hideLauncher()
      startWidget(auth.token, cid)
    } finally {
      opening = false
    }
  }

  button.addEventListener("click", () => {
    void handleLauncherClick()
  })

  const attach = () => {
    document.body.appendChild(container)
  }
  if (document.body) {
    attach()
  } else {
    document.addEventListener("DOMContentLoaded", attach)
  }
}

/**
 * `document.currentScript` is null for async/defer scripts (and ES modules).
 * Prefer the loading tag when it carries a slug; otherwise find the embed tag
 * by attribute — same pattern as packages/embedded-sdk.
 */
export function findBootScript(
  currentScript: { getAttribute(name: string): string | null } | null,
): { getAttribute(name: string): string | null } | null {
  if (currentScript?.getAttribute("data-partner-slug")?.trim()) {
    return currentScript
  }
  if (typeof document === "undefined") return null
  return document.querySelector("script[data-partner-slug]")
}

export function maybeAutoboot(sdk: AutobootSDK): void {
  const config = parseAutobootConfig(findBootScript(document.currentScript))
  if (!config) return
  // Run-once across the page: a second embed <script> (or a double include)
  // reloads this bundle but shares window — bail so we don't stack two launchers
  // and two lifecycles.
  const w = window as unknown as Record<string, unknown>
  if (w.__palazzoVinciWidgetBooted) return
  w.__palazzoVinciWidgetBooted = true
  bootLauncher(sdk, config)
}
